import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import { white } from 'web-ui-kit/colors';
import { dislikeButtonClicked, likeButtonClicked } from 'web-ui-kit/LikeDislikeButtonEvents';
import DislikeHeart from 'web-ui-kit/assets/DislikeHeart';
import LikeHeart from 'web-ui-kit/assets/LikeHeart';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;

var COLOR_HOVER = '#646468';

function LikeDislikeButton(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function (_ref2) {
    var type = _ref2.type,
        payload = _ref2.payload;
  } : _ref$dispatch,
      _ref$isDisliked = _ref.isDisliked,
      isDisliked = _ref$isDisliked === void 0 ? false : _ref$isDisliked,
      _ref$isLiked = _ref.isLiked,
      isLiked = _ref$isLiked === void 0 ? false : _ref$isLiked,
      _ref$dislikeButtonCon = _ref.dislikeButtonConfig,
      dislikeButtonConfig = _ref$dislikeButtonCon === void 0 ? 'hover' : _ref$dislikeButtonCon;

  var handleDislikeButtonClicked = function handleDislikeButtonClicked(jsEvent) {
    dispatch(dislikeButtonClicked({
      isSelected: isDisliked,
      jsEvent: jsEvent
    }));
  };

  var handleLikeButtonClicked = function handleLikeButtonClicked(jsEvent) {
    dispatch(likeButtonClicked({
      isSelected: isLiked,
      jsEvent: jsEvent
    }));
  };

  var currentState = isLiked ? 'liked' : isDisliked ? 'disliked' : 'neutral';
  var showDislikeButton = (currentState === 'neutral' || currentState === 'disliked') && dislikeButtonConfig !== 'never';
  var showLikeButton = currentState === 'neutral' || currentState === 'liked' || dislikeButtonConfig === 'never';
  var Container = styles.Container,
      DisikeButton = styles.DisikeButton,
      LikeButton = styles.LikeButton;
  return (
    /*#__PURE__*/
    React.createElement(Container, {
      className: className
    }, showDislikeButton &&
    /*#__PURE__*/
    React.createElement(DisikeButton, {
      "data-testid": "dislike-button-".concat(isDisliked ? 'disliked' : 'neutral'),
      dislikeButtonConfig: dislikeButtonConfig,
      isSelected: isDisliked,
      onClick: handleDislikeButtonClicked
    },
    /*#__PURE__*/
    React.createElement(DislikeHeart, {
      isSelected: isDisliked
    })), showLikeButton &&
    /*#__PURE__*/
    React.createElement(LikeButton, {
      "data-testid": "like-button-".concat(isLiked ? 'liked' : 'neutral'),
      isSelected: isLiked,
      onClick: handleLikeButtonClicked
    },
    /*#__PURE__*/
    React.createElement(LikeHeart, {
      isSelected: isLiked
    })))
  );
}

LikeDislikeButton.propTypes = {
  /** Allows extending styles with styled(LikeDislikeButton) constructor */
  className: PropTypes.string,

  /** For reporting events up to parent */
  dispatch: PropTypes.func,

  /** Whether or not the item is disliked. */
  isDisliked: PropTypes.bool,

  /** Whether or not the item is liked. */
  isLiked: PropTypes.bool,

  /** Default behavior is to hide the dislike button on devices
    * with primary input supporting hover and then show the
    * dislike button when the like button is hovered.
    * For devices with primary input not supporting hover we
    * just go ahead and show both buttons all the time.
    * This prop allows overriding this default behavior
    * and always showing both buttons.
    */
  dislikeButtonConfig: PropTypes.oneOf(['hover', 'always', 'never'])
}; // Common button styles.
// This is a fixed size buttons with centered icons.

var buttonStyles = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  height: 2rem;\n  width: 1.875rem;\n\n  display: flex;\n  align-items: center;\n  justify-content: center;\n\n  background-color: ", ";\n  border: none;\n  border-radius: 4px;\n  cursor: pointer;\n  margin: 0;\n  outline: none;\n  padding: 0;\n"])), white); // Common hover styles for changing the icon
// color when its button is hovered

var hoverStyles = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  @media (hover: hover) {\n    &:hover {\n      path {\n        fill: ", ";\n      }\n    }\n  }\n"])), COLOR_HOVER);
var styles = {
  Container: styled.div(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    display: flex;\n\n    background-color: ", ";\n    border-radius: 4px;\n\n    /**\n     * on devices where the primary input device supports hover\n     * we will initially hide the dislike button and then\n     * unhide when the control is hovered.\n     */\n    @media (hover: hover) {\n      &:hover {\n        button {\n          display: flex;\n        }\n      }\n    }\n  "])), white),
  DisikeButton: styled.button(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    ", ";\n\n    /**\n     * on devices where the primary input device supports hover\n     * we will initially hide the dislike button and then\n     * unhide when the control is hovered.\n     */\n    ", ";\n\n     /** apply hover styles only when item is not selected */\n     ", ";\n  "])), buttonStyles, function (p) {
    return !p.isSelected && p.dislikeButtonConfig !== 'always' && "\n      @media (hover: hover) {\n        display: none;\n      }\n    ";
  }, function (p) {
    return !p.isSelected && hoverStyles;
  }),
  LikeButton: styled.button(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    ", ";\n\n    /** apply hover styles only when item is not selected */\n     ", ";\n\n    /**\n     * Push liked heart down when not selected so it\n     * better aligns with the disliked heart.\n     */\n    ", ";\n  "])), buttonStyles, function (p) {
    return !p.isSelected && hoverStyles;
  }, function (p) {
    return !p.isSelected && "\n      svg { padding-top: 1px; }\n    ";
  })
};
export default LikeDislikeButton;